import React, {useState, useCallback} from 'react';
import {View, FlatList, StyleSheet, Alert, RefreshControl, Image} from 'react-native';
import {Searchbar, Card, Text, IconButton, Snackbar, Chip} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import AddStockModal from './AddStockModal';
import {colors, spacing} from '../../theme';

const ReadymadesTab = ({selectedOrgUnitId}) => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {readymades, isLoading, fetchReadymades, deleteReadymade} = useInventoryCatalogStore();
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});
  const [stockModal, setStockModal] = useState({visible: false, item: null});

  useFocusEffect(
    useCallback(() => {
      fetchReadymades(search || undefined, selectedOrgUnitId);
    }, [fetchReadymades, search, selectedOrgUnitId]),
  );

  const handleSearch = useCallback((text) => {
    setSearch(text);
    fetchReadymades(text || undefined, selectedOrgUnitId);
  }, [fetchReadymades, selectedOrgUnitId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchReadymades(search || undefined, selectedOrgUnitId);
    } finally {
      setRefreshing(false);
    }
  }, [fetchReadymades, search, selectedOrgUnitId]);

  const handleEdit = useCallback((item) => {
    navigation.navigate('AddEditReadymade', {item});
  }, [navigation]);

  const handleDelete = useCallback((item) => {
    Alert.alert(
      t('common.confirm'),
      t('inventory.deleteConfirm', {name: item.name}),
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteReadymade(item.id);
              setSnackbar({visible: true, message: 'Readymade deleted'});
            } catch (e) {
              setSnackbar({visible: true, message: e.response?.data?.message || t('common.error')});
            }
          },
        },
      ],
    );
  }, [t, deleteReadymade]);

  const handleAddStock = useCallback((item) => {
    setStockModal({visible: true, item});
  }, []);

  const renderItem = useCallback(({item}) => {
    const displayStock = selectedOrgUnitId != null && item.orgUnitStock != null
      ? item.orgUnitStock
      : item.currentStock;

    return (
      <Card style={styles.card}>
        <View style={styles.cardTop}>
          {item.mainImageUrl ? (
            <Image source={{uri: item.mainImageUrl}} style={styles.readymadeThumb} />
          ) : (
            <View style={styles.readymadeThumbPlaceholder}>
              <Icon name="image-outline" size={28} color={colors.textSecondary} />
            </View>
          )}
          <View style={styles.cardLeft}>
            <Text style={styles.itemName} numberOfLines={1}>{item.name}</Text>
            {item.readymadeTypeName && (
              <Text style={styles.itemType}>{item.readymadeTypeName}</Text>
            )}
            <View style={styles.chipRow}>
              {item.sku && <Chip style={styles.chip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{item.sku}</Text></Chip>}
              {item.sizeCodes && item.sizeCodes.length > 0 && item.sizeCodes.map((code, idx) => (
                <Chip key={idx} style={styles.sizeChip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{code}</Text></Chip>
              ))}
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.stockText}>
                {t('inventory.qty')}: {displayStock}
              </Text>
              {item.sellingPrice != null && (
                <Text style={styles.price}>{t('inventory.price')}: ₹{item.sellingPrice}</Text>
              )}
            </View>
          </View>
        </View>
        <View style={styles.cardActions}>
          <IconButton icon="plus-circle-outline" size={22} onPress={() => handleAddStock(item)} />
          <IconButton icon="pencil-outline" size={22} onPress={() => handleEdit(item)} />
          <IconButton icon="delete-outline" size={22} iconColor={colors.error} onPress={() => handleDelete(item)} />
        </View>
      </Card>
    );
  }, [t, handleEdit, handleDelete, handleAddStock, selectedOrgUnitId]);

  const keyExtractor = useCallback((item) => String(item.id), []);

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder={t('common.search')}
          value={search}
          onChangeText={handleSearch}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
          iconColor={colors.textSecondary}
        />
      </View>
      <FlatList
        data={readymades}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          !isLoading && <Text style={styles.emptyText}>{t('inventory.noReadymades')}</Text>
        }
      />
      <AddStockModal
        visible={stockModal.visible}
        item={stockModal.item}
        defaultOrgUnitId={selectedOrgUnitId}
        onDismiss={() => setStockModal({visible: false, item: null})}
        onSuccess={() => {
          setStockModal({visible: false, item: null});
          fetchReadymades(search || undefined, selectedOrgUnitId);
        }}
      />
      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1},
  searchContainer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
    elevation: 2,
  },
  searchbar: {
    backgroundColor: colors.background,
    elevation: 0,
  },
  searchInput: {
    fontSize: 14,
  },
  list: {paddingHorizontal: spacing.sm, paddingBottom: spacing.xl + 60},
  card: {marginBottom: spacing.sm, backgroundColor: colors.surface, padding: spacing.sm, borderRadius: 10},
  cardTop: {flexDirection: 'row', alignItems: 'center'},
  readymadeThumb: {width: '20%', aspectRatio: 1, borderRadius: 8, marginRight: spacing.sm},
  readymadeThumbPlaceholder: {width: '20%', aspectRatio: 1, borderRadius: 8, marginRight: spacing.sm, backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center'},
  cardLeft: {flex: 1},
  itemName: {fontSize: 16, fontWeight: '600', color: colors.text},
  itemType: {fontSize: 13, color: colors.textSecondary, marginTop: 2},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: spacing.xs, gap: spacing.xs},
  chip: {height: 26, paddingVertical: 0},
  sizeChip: {height: 26, paddingVertical: 0, backgroundColor: colors.primary + '15'},
  chipText: {fontSize: 11, lineHeight: 16},
  infoRow: {flexDirection: 'row', alignItems: 'center', marginTop: spacing.xs, gap: spacing.md},
  stockText: {fontSize: 14, color: colors.text},
  price: {fontSize: 14, fontWeight: '500', color: colors.primary},
  cardActions: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: spacing.xs},
  emptyText: {textAlign: 'center', marginTop: spacing.xl, color: colors.textSecondary},
});

export default ReadymadesTab;
